import React from 'react';

interface ResidueInfo {
  chain: string;
  resNum: number;
  resName: string;
  index: number;
}

interface SequenceData {
  chain: string;
  sequence: string;
  residues: ResidueInfo[];
}

interface EnhancedSequenceDisplayProps {
  sequences: SequenceData[];
  highlightedResidues?: string[]; // Array of chain-position keys like "A-123"
  highlightColor?: 'orange' | 'red';
  useOriginalSequence?: boolean;
  originalSequence?: string;
  // Optional: limit visible rows and enable scroll so it cuts after a full row
  maxRows?: number;
}

// Convert 3-letter amino acid codes to 1-letter codes
const AMINO_ACID_MAP: { [key: string]: string } = {
  'ALA': 'A', 'ARG': 'R', 'ASN': 'N', 'ASP': 'D', 'CYS': 'C',
  'GLN': 'Q', 'GLU': 'E', 'GLY': 'G', 'HIS': 'H', 'ILE': 'I',
  'LEU': 'L', 'LYS': 'K', 'MET': 'M', 'PHE': 'F', 'PRO': 'P',
  'SER': 'S', 'THR': 'T', 'TRP': 'W', 'TYR': 'Y', 'VAL': 'V',
  // Handle some common variations
  'MSE': 'M', // Selenomethionine
  'SEC': 'U', // Selenocysteine
  'PYL': 'O', // Pyrrolysine
};

const getAminoAcidLetter = (resName: string): string => {
  return AMINO_ACID_MAP[resName.toUpperCase()] || 'X';
};

export function EnhancedSequenceDisplay({ 
  sequences, 
  highlightedResidues = [], 
  highlightColor = 'orange',
  useOriginalSequence = false,
  originalSequence = '',
  maxRows
}: EnhancedSequenceDisplayProps) {
  const highlightedSet = new Set(highlightedResidues);
  
  // Color mapping
  const colors = {
    orange: '#fb923c', // orange-400
    red: '#f87171'     // red-400
  };

  // If using original sequence, we need to create a sequence from the original data
  const displaySequences = useOriginalSequence && originalSequence 
    ? sequences.map(seq => ({
        ...seq,
        sequence: originalSequence.replace(/_/g, ''), // Remove underscores
        residues: seq.residues.map((residue, idx) => ({
          ...residue,
          resName: originalSequence[idx] === '_' ? residue.resName : (originalSequence[idx] || 'X') // Don't use underscore, use actual residue name
        }))
      }))
    : sequences;

  // Each line renders at text-lg with default/explicit leading of 1.75rem (28px)
  const rowPx = 28; // pixels per line at leading-7
  const scrollStyle = maxRows ? { maxHeight: `${rowPx * maxRows}px`, overflowY: 'auto', paddingRight: '0.5rem' } as React.CSSProperties : undefined;

  return (
    <div className="space-y-8">
      {displaySequences.map((seq) => (
        <div key={seq.chain}>
          <div className="text-base text-gray-500 mb-2">Chain {seq.chain}</div>
          <div className="relative pb-4">
            <div style={scrollStyle}>
            <div className="font-mono text-lg leading-7 select-none inline-block pr-4">
              {seq.residues.map((residue, idx) => {
                const key = `${residue.chain}-${residue.resNum}`;
                const isHighlighted = highlightedSet.has(key);
                
                // For original sequence, use the character directly (but not asterisks), for generated use conversion
                const displayChar = useOriginalSequence && originalSequence
                  ? (originalSequence[idx] === '_' ? getAminoAcidLetter(residue.resName) : (originalSequence[idx] || 'X'))
                  : getAminoAcidLetter(residue.resName);
                
                return (
                  <span key={key}>
                    <span
                      className="inline-block px-1.5 pb-0 select-none relative text-gray-700"
                      title={`${useOriginalSequence ? originalSequence[idx] : residue.resName} ${residue.resNum}`}
                    >
                      {displayChar}
                      {/* Colored underline for highlighted residues */}
                      {isHighlighted && (
                        <div
                          className="absolute"
                          style={{
                            bottom: '0px',
                            left: '0',
                            right: '0',
                            height: '3px',
                            backgroundColor: colors[highlightColor],
                          }}
                        />
                      )}
                    </span>
                    {(idx + 1) % 10 === 0 && (
                      <span className="text-base text-gray-400 ml-0.5 mr-1">{residue.resNum}</span>
                    )}
                  </span>
                );
              })}
            </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
